import { find } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AdditionalOrderColumn,
  FilterDefinitionType,
  OrderStatus,
  OrdersTab,
} from 'enums';
import { Orders } from 'features/orders';
import {
  useWhiteTriangle,
  useCurrencies,
  useOrderQueryFilters,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { OrderFilters, FilterDefinition } from 'types';
import { formUtils, orderUtils, requisitesUtils } from 'utils';

export const TraderActiveOrders: React.FC = () => {
  const { t: tFilters } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.filters',
  });

  const { paymentTypes, banks } = useUserContext();
  const { getFiatCurrencyCode, fiatCurrenciesOptions } = useCurrencies();
  const { isWhiteTriangle, requisitesId } = useWhiteTriangle();

  useOrderQueryFilters({
    ...(isWhiteTriangle && { requisitesId }),
  });

  const additionalFilters: FilterDefinition<OrderFilters>[] = useMemo(
    () => [
      {
        label: tFilters('status'),
        name: 'status',
        type: FilterDefinitionType.OrderStatus,
        orderStatuses: [OrderStatus.TraderConfirm, OrderStatus.CustomerConfirm],
        getDisplayName: (value: OrderStatus) =>
          orderUtils.getStatusLabel(value),
      },
      {
        label: tFilters('payment_type'),
        name: 'paymentTypeId',
        type: FilterDefinitionType.Select,
        options: requisitesUtils.getPaymentTypesOptions(paymentTypes),
        hidden: isWhiteTriangle,
        getDisplayName: (value: string) =>
          requisitesUtils.getPaymentTypeLabel(
            find(paymentTypes, { id: value })!,
          ),
      },
      {
        label: tFilters('bank'),
        name: 'bankId',
        type: FilterDefinitionType.Select,
        hidden: isWhiteTriangle,
        options: formUtils.getOptions(banks),
        getDisplayName: (value: string) => find(banks, { id: value })?.name,
      },
      {
        label: tFilters('fiat_currency'),
        name: 'fiatCurrencyId',
        hidden: isWhiteTriangle,
        type: FilterDefinitionType.Select,
        options: fiatCurrenciesOptions,
        getDisplayName: getFiatCurrencyCode,
      },
      {
        label: tFilters('customer_bank'),
        name: 'customerBank',
        hidden: isWhiteTriangle,
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(banks, 'name', 'code'),
        getDisplayName: (value: string) => find(banks, { code: value })?.name,
      },
      ...(!isWhiteTriangle
        ? requisitesUtils.getRequisitesFieldsFilters<OrderFilters>()
        : []),
    ],
    [
      banks,
      fiatCurrenciesOptions,
      getFiatCurrencyCode,
      paymentTypes,
      tFilters,
      isWhiteTriangle,
    ],
  );

  return (
    <Orders
      tab={OrdersTab.Active}
      additionalFilters={additionalFilters}
      additionalColumns={[
        AdditionalOrderColumn.StatusActive,
        AdditionalOrderColumn.TraderCard,
        AdditionalOrderColumn.Customer,
      ]}
    />
  );
};
